import type { ParsedUrlQuery } from 'querystring';

import {
    BlockList,
    getPost,
    initializeApollo,
    PlacementProvider,
} from '@zillow/cms-blocks';
import { GetStaticPaths, GetStaticProps, GetStaticPropsResult } from 'next';
import React from 'react';

import { REVALIDATION_INTERVAL } from '../../server/config';
import Footer from '../components/Homeloans/Footer';
import Layout from '../components/Layout/Layout';
import type { TMenuItem } from '../components/Menu/Menu.types';
import { Page, Post } from '../types/shared';

import { processBlocksData } from './blocks';
import { shouldUseVisualRefreshTheme } from './document';
import {
    getStateRatesBlocks,
    STATE_RATES_TEMPLATE_URI,
} from './mortgageRatesBlocks';
import {
    detectMortgageRatesPageType,
    generateStateHeadContent,
    USState,
} from './mortgageRatesState';
import { withD } from './withDynamicThing';

// Layout component props
export type MortgageRatesLayoutProps = {
    children: React.ReactNode;
    head: string;
    menu?: { items: TMenuItem[] };
    preview?: boolean;
    stateData?: USState;
    isStateRatesPage?: boolean;
};

// Content component props
export type MortgageRatesContentProps = {
    post: (Post | Page) & { menu?: { items: TMenuItem[] } | null };
    preview?: boolean;
    useVisualRefreshTheme?: boolean;
    stateData?: USState;
    isStateRatesPage?: boolean;
};

// Content component that follows homeloans pattern
const MortgageRatesContent: React.FC<MortgageRatesContentProps> = ({
    post,
    isStateRatesPage,
}) => {
    return (
        <>
            {isStateRatesPage ? (
                <PlacementProvider placement="page">
                    <BlockList blocks={post?.blocks || []} />
                </PlacementProvider>
            ) : (
                <BlockList blocks={post?.blocks || []} />
            )}
            <Footer />
        </>
    );
};

// Layout component that follows homeloans pattern
const MortgageRatesLayout: React.FC<MortgageRatesLayoutProps> = (props) => {
    // For state pages, use generated state head content
    const head =
        props.isStateRatesPage && props.stateData
            ? generateStateHeadContent(
                  props.stateData.name,
                  props.stateData.slug
              )
            : props.head;

    return (
        <Layout head={head} menu={props.menu} preview={props.preview}>
            {props.children}
        </Layout>
    );
};

// Shared logic for fetching page props (similar to buildPrimaryPostPage)
async function fetchMortgageRatesPageProps({
    params,
    isStatic = false,
}: {
    params: ParsedUrlQuery | undefined;
    isStatic?: boolean;
}): Promise<GetStaticPropsResult<MortgageRatesContentProps>> {
    const { slug = [] } = params || {};
    const slugArray = Array.isArray(slug) ? slug : [slug];
    const slugString = slugArray.join('/');

    const detection = detectMortgageRatesPageType(slugString);

    initializeApollo();

    if (detection.type === 'state-rates') {
        const blocks = await getStateRatesBlocks(detection.stateData);

        const post = {
            id: `state-rates-${detection.stateData.slug}`,
            type: 'page' as const,
            status: 'publish',
            title: { raw: `${detection.stateData.name} Mortgage Rates` },
            blocks,
            head: generateStateHeadContent(
                detection.stateData.name,
                detection.stateData.slug
            ),
            link: `/homeloans/mortgage-rates/${detection.stateData.slug}/`,
            slug: detection.stateData.slug,
            menu: null,
        };

        const path =
            '/homeloans/mortgage-rates' +
            (slugString ? '/' + slugString + '/' : '/');

        const props: MortgageRatesContentProps = {
            post: post as (Post | Page) & {
                menu?: { items: TMenuItem[] } | null;
            },
            stateData: detection.stateData,
            isStateRatesPage: true,
            useVisualRefreshTheme: shouldUseVisualRefreshTheme(path),
        };

        if (isStatic) {
            return {
                props,
                revalidate: REVALIDATION_INTERVAL,
            };
        }

        return { props };
    }

    // Handle CMS pages (fallback)
    if (detection.type === 'cms-page') {
        try {
            const postId = slugString
                ? `mortgage-rates/${slugString}`
                : 'mortgage-rates';

            // WP resolves URIs case-insensitively, so /State-Template/ would
            // otherwise render the raw template.
            if (postId.toLowerCase() === STATE_RATES_TEMPLATE_URI) {
                return { notFound: true };
            }

            const { post, settings } = await getPost({
                variables: {
                    site: 'ZHL',
                    id: postId,
                    idType: 'URI',
                    withSettings: true,
                },
                fetchPolicy: 'no-cache',
            });

            if (post?.status !== 'publish') {
                return { notFound: true };
            }

            const blocks = await processBlocksData(post);
            const footerBlocks = await processBlocksData(post?.footer);
            const sidebarBlocks = await processBlocksData(post?.sidebar);

            const menu = post?.type === 'post' ? settings?.menu : post?.menu;

            const path =
                '/homeloans/mortgage-rates' +
                (slugString ? '/' + slugString + '/' : '/');

            const props: MortgageRatesContentProps = {
                post: {
                    ...post,
                    blocks,
                    footer:
                        footerBlocks.length > 0
                            ? { ...post.footer, blocks: footerBlocks }
                            : null,
                    sidebar:
                        sidebarBlocks.length > 0
                            ? { ...post.sidebar, blocks: sidebarBlocks }
                            : null,
                    menu: (menu as { items: TMenuItem[] }) || null,
                },
                useVisualRefreshTheme: shouldUseVisualRefreshTheme(path),
            };

            if (isStatic) {
                return {
                    props,
                    revalidate: REVALIDATION_INTERVAL,
                };
            }

            return { props };
        } catch (error) {
            console.error('Error fetching CMS page:', error);
            return { notFound: true };
        }
    }

    return { notFound: true };
}

/**
 * Build mortgage rates pages following the exact homeloans pattern
 * with added validation for state-specific pages
 */
export async function buildMortgageRatesPage() {
    const getStaticProps: GetStaticProps<MortgageRatesContentProps> = async (
        context
    ) => {
        return fetchMortgageRatesPageProps({
            params: context.params,
            isStatic: true,
        });
    };

    const getStaticPaths: GetStaticPaths = async () => {
        // On-demand SSG: don't prebuild state pages to avoid build-time network calls
        const paths: Array<{ params: { slug: string[] } }> = [];
        return {
            paths,
            fallback: 'blocking',
        };
    };

    const PageComponent: React.FC<MortgageRatesContentProps> = ({
        post,
        preview,
        stateData,
        isStateRatesPage,
    }) => {
        const layoutProps: MortgageRatesLayoutProps = {
            head: post.head || '',
            children: (
                <MortgageRatesContent
                    post={post}
                    preview={preview}
                    stateData={stateData}
                    isStateRatesPage={isStateRatesPage}
                />
            ),
            preview,
            stateData,
            isStateRatesPage,
        };

        // Add menu props if available
        if (post?.menu) {
            layoutProps.menu = post.menu;
        }

        return <MortgageRatesLayout {...layoutProps} />;
    };

    const WrappedPage = await withD(PageComponent);

    return {
        getStaticProps,
        getStaticPaths,
        Page: PageComponent,
        WrappedPage,
    };
}
